import { SmoothLineChart } from '../../../components/Charts';
import dayjs from 'dayjs';
import { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import IncreaseIcon from '../../../assets/icons/increase.svg?react';
import { useSetUpList } from '../../../hooks/queries/setUpQueries';
const DrResourceChart = ({ dashboardData = {}, type = 'power' }) => {
  const { data: setUpData } = useSetUpList();
  const defaultCblModuleName = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_NAME')?.Value;
  const defaultCblModuleVersion = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_VERSION')?.Value;

  // props로 받은 통합 데이터 사용
  const {
    predictionCblData,
    powerMeterDemandData,
    livePowerMeterDemandData,
    liveCblData,
    drEventPowerMeterDemandData,
    isLoading,
    hasError,
    drHistoryLatestData,
    drResources,
    selectedRegion,
    lastUpdateTime,
    nextUpdateTime,
    isUpdating,
    manualUpdate
  } = dashboardData;
  const [cssColors, setCssColors] = useState({
    gray600: '#718096',
    graphSetZ1: '#8C62FF',
    graphSetZ2: '#FB85A1',
    graphSetZ3: '#38BDF8',
    graphSetZ4: '#FEC02E',
  });
  // DR 발령일이 오늘인지 확인
  const drStartDate = dayjs(drHistoryLatestData?.date);
  // const today = dayjs();
  const today = dayjs().format('YYYY-MM-DD');
  const isToday = drStartDate?.isSame(today, 'day');

  // lastUpdateTime에서 타임슬롯(P00~P95) 계산
  const getCurrentTimeSlot = () => {
    if (!lastUpdateTime) return 'P00';
    
    const hour = lastUpdateTime.hour();
    const minute = lastUpdateTime.minute();
    
    // 15분 단위로 내림 (0, 15, 30, 45분)
    let flooredMinute;
    if (minute < 15) {
      flooredMinute = 0;
    } else if (minute < 30) {
      flooredMinute = 15;
    } else if (minute < 45) {
      flooredMinute = 30;
    } else {
      flooredMinute = 45;
    }
    
    const timeIndex = hour * 4 + (flooredMinute / 15);
    return `P${timeIndex.toString().padStart(2, '0')}`;
  };
  
  const currentTimeSlot = getCurrentTimeSlot();


  // 15분 단위로 96개 데이터 포인트 생성
  const generateTimeLabels = () => {
    const labels = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let quarter = 0; quarter < 4; quarter++) {
        const minutes = quarter * 15;
        labels.push(`${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
      }
    }
    return labels;
  };

  // 마지막 업데이트 시간 계산 (15분 단위로 반올림, 10초 지연)
  const getLastUpdateTime = () => {
    if (lastUpdateTime) {
      return lastUpdateTime;
    }
    const now = dayjs();
    const currentMinute = now.minute();
    const currentSecond = now.second();
    
    // 현재 분을 15분 단위로 내림
    const lastUpdateMinute = Math.floor(currentMinute / 15) * 15;
    
    return now.minute(lastUpdateMinute).second(10).millisecond(0);
  };

  // 실제 전력 수요 데이터를 15분 단위로 변환 (useCallback으로 최적화)
  const transformPowerMeterData = useCallback((powerMeterData, isRealtime = false) => {
    // 데이터가 없으면 빈 객체 반환
    if (!powerMeterData || typeof powerMeterData !== 'object') {
      return { labels: [], data: [] };
    }

    // API 데이터에서 TimeSlotAverageValue 추출
    const timeSlotData = powerMeterData?.TimeSlotSumValue || powerMeterData;

    const labels = [];
    const data = [];
    
    // 실시간 데이터인 경우 마지막 업데이트 시간 계산
    let lastUpdateIndex = 95; // 기본적으로 전체 24시간 (P00 ~ P95)
    
    if (isRealtime) {
      // 마지막 업데이트 시간까지의 인덱스 계산
      const lastUpdateTime = getLastUpdateTime();
      const lastUpdateHour = lastUpdateTime.hour();
      const lastUpdateMinute = lastUpdateTime.minute();
      lastUpdateIndex = lastUpdateHour * 4 + (lastUpdateMinute / 15);
    }

    // 항상 전체 24시간 (P00 ~ P95) 생성
    for (let i = 0; i <= 95; i++) {
      const hour = Math.floor(i / 4); // 4개씩 그룹화 (15분 * 4 = 1시간)
      const minute = (i % 4) * 15; // 0, 15, 30, 45분
      
      // 시간 형식: HH:MM
      const timeLabel = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      
      // P00, P01, P02... 형태의 키 생성
      const key = `P${i.toString().padStart(2, '0')}`;
      
      labels.push(timeLabel);
      
      // 실시간 데이터인 경우 마지막 업데이트 시간 이후는 null
      if (isRealtime && i > lastUpdateIndex) {
        data.push(null);
      } else {
        data.push(timeSlotData[key] || 0);
      }
    }
    
    return { labels: labels, data: data };
  }, [lastUpdateTime]);

  useEffect(() => {
    const getCSSVariable = (variable) => {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(variable)
        .trim();
    };
    setCssColors({
      gray600: getCSSVariable('--gray-600'),
      graphSetZ1: getCSSVariable('--graph-set-z1'),
      graphSetZ2: getCSSVariable('--graph-set-z2'),
      graphSetZ3: getCSSVariable('--graph-set-z3'),
      graphSetZ4: getCSSVariable('--graph-set-z4'),
    });
  }, []);

  // 실시간 충전 수요 차트 데이터 (useMemo로 데이터가 준비된 후에만 생성)
  const chartData = useMemo(() => {

    // 각 데이터셋의 변환된 데이터 가져오기
    const liveData = transformPowerMeterData(livePowerMeterDemandData, true);
    const powerData = transformPowerMeterData(powerMeterDemandData, false);
    const predictionData = transformPowerMeterData(predictionCblData, false);
    const liveCbl = transformPowerMeterData(liveCblData, true);
    const drEventData = transformPowerMeterData(drEventPowerMeterDemandData, false);
    // type에 따라 datasets 구성
    let datasets = [];
    
    if (type === 'power') {
      // power 타입: livePowerMeterDemandData, powerMeterDemandData
      datasets = [
        {
          label: '실시간 전력수요 (kWh)',
          data: liveData.data,
          borderColor: cssColors.graphSetZ3,
          backgroundColor: cssColors.graphSetZ3,
          fill: false,
          tension: 0.1,
          borderWidth: 2,  // 라인 굵기 조정
          pointRadius: 2,
          pointHoverRadius: 3,
          
        },
        {
          label: '어제 전력수요 (kWh)',
          data: powerData.data,
          borderColor: cssColors.graphSetZ4,
          backgroundColor: cssColors.graphSetZ4,
          fill: false,
          tension: 0.1,
          pointRadius: 0,
          borderWidth: 2,  // 라인 굵기 조정
          pointRadius: 2,
          pointHoverRadius: 3,
        }
      ];
    } else if (type === 'cbl') {
      // cbl 타입: livePowerMeterDemandData, predictionCblData, liveCblData
      datasets = [
        {
          label: '실시간 전력수요 (kWh)',
          data: liveData.data,
          borderColor: cssColors.graphSetZ3,
          backgroundColor: cssColors.graphSetZ3,
          fill: false,
          tension: 0.1,
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 3,
        },
        {
          label: 'CBL (kWh)',
          data: predictionData.data,
          borderColor: cssColors.graphSetZ1,
          backgroundColor: cssColors.graphSetZ1,
          fill: false,
          tension: 0.1,
          borderDash: [5, 5],  // 점선 패턴 [선 길이, 간격 길이]
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 3,
        },
        {
          label: '실시간 CBL (kWh)',
          data: liveCbl.data,
          borderColor: cssColors.graphSetZ2,
          backgroundColor: cssColors.graphSetZ2,
          fill: false,
          tension: 0.1,
          borderDash: [5, 5],  // 점선 패턴 [선 길이, 간격 길이]
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 3,
        }
      ];
    } else if (type === 'dr') {
      datasets = [
        {
          label: '실시간 전력수요 (kWh)',
          data: liveData.data,
          borderColor: cssColors.graphSetZ3,
          backgroundColor: cssColors.graphSetZ3,
          fill: false,
          tension: 0.1,
          borderWidth: 2,  // 라인 굵기 조정
          pointRadius: 2,
          pointHoverRadius: 3,
        },
        {
          label: 'DR 발령일 전력수요 (kWh)',
          data: drEventData.data,
          borderColor: cssColors.graphSetZ4,
          backgroundColor: cssColors.graphSetZ4,
          fill: false,
          tension: 0.1,
          borderWidth: 2,  // 라인 굵기 조정
          pointRadius: 2,
          pointHoverRadius: 3,
        }
      ];
    }

    return {
      labels: liveData.labels.length > 0 ? liveData.labels : 
              powerData.labels.length > 0 ? powerData.labels : 
              predictionData.labels.length > 0 ? predictionData.labels : 
              generateTimeLabels(),
      datasets
    };
  }, [type, predictionCblData, powerMeterDemandData, livePowerMeterDemandData, liveCblData, transformPowerMeterData]);

  // 현재 시각을 15분 단위로 내림
  const getCurrentTimeIndex = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // 15분 단위로 내림 (0, 15, 30, 45분)
    let flooredMinute;
    if (currentMinute < 15) {
      flooredMinute = 0;
    } else if (currentMinute < 30) {
      flooredMinute = 15;
    } else if (currentMinute < 45) {
      flooredMinute = 30;
    } else {
      flooredMinute = 45;
    }
    
    const timeIndex = currentHour * 4 + (flooredMinute / 15);
    return Math.min(timeIndex, 95); // 최대 95 (23:45)
  };

  const currentTimeIndex = getCurrentTimeIndex();

  // 현재 시각을 dayjs 객체로 변환
  const getCurrentTimeAsDayjs = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // 15분 단위로 내림 (0, 15, 30, 45분)
    let flooredMinute;
    if (currentMinute < 15) {
      flooredMinute = 0;
    } else if (currentMinute < 30) {
      flooredMinute = 15;
    } else if (currentMinute < 45) {
      flooredMinute = 30;
    } else {
      flooredMinute = 45;
    }
    
    // dayjs 객체 생성 (SmoothLineChart에서 사용)
    return dayjs().hour(currentHour).minute(flooredMinute).second(0).millisecond(0);
  };

  const currentTime = getCurrentTimeAsDayjs();
  const chartRef = useRef(null);

  // DR 발령 시간대 정보
  const getDRDispatchInfo = () => {
    if (!drHistoryLatestData?.timeSlots || !Array.isArray(drHistoryLatestData.timeSlots)) {
      return null;
    }

    // timeSlots를 숫자로 변환하고 정렬
    const slotNumbers = drHistoryLatestData.timeSlots
      .map(timeSlot => parseInt(timeSlot.replace('P', '')))
      .filter(slot => slot >= 0 && slot <= 95)
      .sort((a, b) => a - b);

    if (slotNumbers.length === 0) {
      return null;
    }

    return {
      startIndex: slotNumbers[0],
      endIndex: slotNumbers[slotNumbers.length - 1],
      timeSlots: drHistoryLatestData.timeSlots
    };
  };

  const drDispatchInfo = getDRDispatchInfo();


  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: cssColors.gray600,
        bodyColor: cssColors.gray600,
        borderColor: 'transparent',
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function(context) {
            return `시간: ${context[0].label}`;
          },
          label: function(context) {
            return `충전 수요: ${context.parsed.y.toLocaleString()}kWh`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          maxTicksLimit: 12,
          color: cssColors.gray600,
          font: {
            size: 11
          },
          callback: function(value, index, ticks) {
            // 정시 단위(00:00, 01:00, 02:00, ...)만 표시
            const hour = Math.floor(index / 4);
            if (index % 4 === 0) {
              return `${hour.toString().padStart(2, '0')}:00`;
            }
            return '';
          }
        }
      },
      y: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          color: cssColors.gray600,
          font: {
            size: 11
          },
          callback: function(value) {
            return value + 'kWh';
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <div
        className="flex flex-col justify-between"
      >
        <h3 className="text-b1-700 text-gray-700">실시간 충전 요구</h3>
        <div className="flex flex-col gap-5">
          <div className="flex align-center gap-5">
            <p className="text-b2-700 text-gray-600">데이터 로딩 중...</p>
          </div>
          <div className="flex-1 dashboard-charging-demand-chart-container flex align-center justify-center">
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 처리
  if (hasError) {
    return (
      <div
        className="flex flex-col justify-between"
      >
      </div>
    );
  }

  return (
    <div
      className="flex flex-col justify-between"
    >
      <div className="flex justify-between">
        <h3 className="text-b1-700 text-gray-700 mb-5">
          {
            type === 'power' && '실시간 - 어제 전력수요 비교'
          }
          {
            type === 'cbl' && '실시간 전력수요 - CBL 비교'
          }
          {
            type === 'dr' && '실시간 전력수요 - DR 발령일 비교'
          }
        </h3>
        <p className="text-c1-400 text-gray-600">
          {lastUpdateTime ? `마지막 집계: ${lastUpdateTime.format('YYYY-MM-DD HH:mm')}` : '데이터 로딩 중...'}
        </p>
      </div>
      <div className="flex flex-col gap-5" style={{ height: '280px' }}>
        <div className="flex align-center gap-5 flex-wrap">
          <p className="text-b2-700 text-gray-600">현재 충전 요구량</p>
            {type === 'power' &&
              <p className="text-b2-700 text-primary">
                {livePowerMeterDemandData?.TimeSlotSumValue?.[currentTimeSlot] && livePowerMeterDemandData.TimeSlotSumValue[currentTimeSlot] !== 0 ? `${livePowerMeterDemandData.TimeSlotSumValue[currentTimeSlot].toLocaleString()} kWh` : '0 kWh'}
              </p>
            }
            {type === 'cbl' &&
              <p className="text-b2-700 text-primary">
              {livePowerMeterDemandData?.TimeSlotSumValue?.[currentTimeSlot] && livePowerMeterDemandData.TimeSlotSumValue[currentTimeSlot] !== 0 ? `${livePowerMeterDemandData.TimeSlotSumValue[currentTimeSlot].toLocaleString()} kWh` : '0 kWh'}
              </p>
            }
            {type === 'dr' &&
              <p className="text-b2-700 text-primary">
              {livePowerMeterDemandData?.TimeSlotSumValue?.[currentTimeSlot] && livePowerMeterDemandData.TimeSlotSumValue[currentTimeSlot] !== 0 ? `${livePowerMeterDemandData.TimeSlotSumValue[currentTimeSlot].toLocaleString()} kWh` : '0 kWh'}
              </p>
            }
          {(() => {
            const liveValue = livePowerMeterDemandData?.TimeSlotSumValue?.[currentTimeSlot];
            
            if (type === 'power') {
              // 어제 대비 %
              const tomorrowValue = powerMeterDemandData?.TimeSlotSumValue?.[currentTimeSlot];
              const diffPercent = ((liveValue - tomorrowValue) / tomorrowValue * 100).toFixed(1);
              const isIncrease = diffPercent > 0;
              return (
                <div className="flex align-center gap-5">
                  <div className={`dashboard-comparison ${isIncrease ? 'increase' : 'decrease'}`}>
                    어제 대비: {isIncrease ? <IncreaseIcon /> : <IncreaseIcon style={{ transform: 'rotate(90deg)' }} />}{diffPercent}%
                  </div>
                </div>

              );
            } else if (type === 'cbl') {
              // 실시간 전력수요와 CBL의 차이 (예측 CBL, 실시간 CBL 각각)
              const predictionCblValue = predictionCblData?.TimeSlotSumValue?.[currentTimeSlot] || 0;
              const liveCblValue = liveCblData?.TimeSlotSumValue?.[currentTimeSlot] || 0;
              const isBigger = (targetValue, compareValue)=>{
                return targetValue > compareValue;
              }
              const isSmaller = (targetValue, compareValue)=>{
                return targetValue < compareValue;
              }
              const predAppliedModuleName = predictionCblData?.AppliedModuleName || defaultCblModuleName;
              const predAppliedModuleVersion = predictionCblData?.AppliedModuleVersion || defaultCblModuleVersion;
              const liveAppliedModuleName = liveCblData?.AppliedModuleName || defaultCblModuleName;
              const liveAppliedModuleVersion = liveCblData?.AppliedModuleVersion || defaultCblModuleVersion;
              return (

                <div className="flex gap-5">
                  {(
                    <div className={`dashboard-comparison ${isBigger(liveValue, predictionCblValue) ? 'increase' : isSmaller(liveValue, predictionCblValue) ? 'decrease' : 'same'}`}>
                      CBL 대비: {isBigger(liveValue, predictionCblValue) ? <IncreaseIcon /> : isSmaller(liveValue, predictionCblValue) ? <IncreaseIcon style={{ transform: 'rotate(90deg)' }} /> : ''}{(liveValue - predictionCblValue).toFixed(3)} kWh
                    </div>
                  )}
                  {(
                    <div className={`dashboard-comparison ${isBigger(liveValue, liveCblValue) ? 'increase' : isSmaller(liveValue, liveCblValue) ? 'decrease' : 'same'}`}>
                      실시간 CBL 대비: {isBigger(liveValue, liveCblValue) ? <IncreaseIcon /> : isSmaller(liveValue, liveCblValue) ? <IncreaseIcon style={{ transform: 'rotate(90deg)' }} /> : ''}{(liveValue - liveCblValue).toFixed(3)} kWh
                    </div>
                  )}
                  <div className={`dashboard-comparison none`}>
                    CBL 모듈 : {`${predAppliedModuleName} (${predAppliedModuleVersion})`}
                  </div>
                  <div className={`dashboard-comparison none`}>
                    실시간 CBL 모듈 : {`${liveAppliedModuleName} (${liveAppliedModuleVersion})`}
                  </div>
                </div>
                
              );
            } else if (type === 'dr') {
              // DR 발령일의 같은 타임슬롯 대비 %
              const drEventValue = drEventPowerMeterDemandData?.TimeSlotSumValue?.[currentTimeSlot];
              if (liveValue && drEventValue && drEventValue !== 0) {
                const diffPercent = ((liveValue - drEventValue) / drEventValue * 100).toFixed(1);
                const isIncrease = diffPercent > 0;
                return (
                  <div className={`dashboard-comparison ${isIncrease ? 'increase' : 'decrease'}`}>
                    DR 발령일 대비: {isIncrease ? <IncreaseIcon /> : <IncreaseIcon style={{ transform: 'rotate(90deg)' }} />}{diffPercent}%
                  </div>
                );
              }
            }
            
            return null;
          })()}
          {type === 'dr' && drHistoryLatestData && (() => {
            return (
              <div className="flex align-center gap-5">
                <p className="text-b2-700 text-gray-600">최근 DR 발령 시간:</p>
                <p className="text-b2-700 text-gray-600">
                  {dayjs(drHistoryLatestData.date).format('YYYY-MM-DD')} {drHistoryLatestData.startTime} - {drHistoryLatestData.endTime}
                </p>
              </div>
            )
          })()}
          {drHistoryLatestData && type !== 'dr' && (() => {
            if (!isToday) {
              return (
                <div className="flex align-center gap-5">
                  <div className='dashboard-comparison none'>
                    DR 발령 없음
                  </div>
                </div>
              );
            }
            
            // 현재 시각을 HH:mm 형식으로 변환
            const now = dayjs();
            const currentTimeStr = now.format('HH:mm');
            const startTime = drHistoryLatestData.startTime;
            const endTime = drHistoryLatestData.endTime;
            
            // 시간 비교를 위해 분으로 변환
            const timeToMinutes = (timeStr) => {
              const [hours, minutes] = timeStr.split(':').map(Number);
              return hours * 60 + minutes;
            };
            
            const currentMinutes = timeToMinutes(currentTimeStr);
            const startMinutes = timeToMinutes(startTime);
            const endMinutes = timeToMinutes(endTime);
            
            // 현재 시각이 어느 범위에 속하는지 판단
            let statusText = 'DR 발령중';
            let statusClass = 'increase'; // 빨간색 (발령중)
            
            if (currentMinutes < startMinutes) {
              // 시작 시간 이전
              statusText = 'DR 발령 예정됨';
              statusClass = 'warning'; // 노란색 (예정)
            } else if (currentMinutes > endMinutes) {
              // 종료 시간 이후
              statusText = 'DR 발령 종료됨';
              statusClass = 'same'; // 회색 (종료)
            }
            
            return (
              <div className="flex align-center gap-5">
                <p className="text-b2-700 text-gray-600">DR 발령 시간:</p>
                <p className="text-b2-700 text-red-500">
                  {startTime} - {endTime}
                </p>
                <div className={`dashboard-comparison ${statusClass}`}>
                  {statusText}
                </div>
              </div>
            );
          })()}
        </div>
          <div className="flex-1 dashboard-charging-demand-chart-container relative" style={{ height: '280px' }}>
            <SmoothLineChart
              ref={chartRef}
              data={chartData}
              options={chartOptions}
              lastUpdateTime={currentTime}
              drDispatchInfo={drDispatchInfo}
              chartBorder={false}
              chartPadding={false}
              height="100%"
              className="charging-demand-chart"
              isToday={type === 'dr' || isToday}
              yMin={0}
            />
          </div>
      </div>
    </div>
  );
};

export default DrResourceChart;
